import { Descriptions, Divider, Drawer, Spin, Tag } from 'antd'
import { useSelector } from 'react-redux'

import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { STANDARD_DRAWER_WIDTH } from '../../../utils/constants'
import { formatDate, formatStampDate, formatStampDateTime, getSeverityTag, getStatusTag, getTypeLabel } from '../../../utils/entities/aphmAnomaly'
import { getStateTag } from '../../../utils/entities/internship'

const TAG_STYLE = { borderRadius: '8px', fontWeight: 500 }

const isDateKey = key => /date$/i.test(key)

const STATE_KEYS = ['state', 'existingState']

const REDUNDANT_KEYS = ['stateAsString']

const isRedundantKey = (key, snapshot) => REDUNDANT_KEYS.includes(key) && snapshot.state !== undefined

const SnapshotValue = ({ name, value }) => {
  const t = useSelector(selectTranslate)

  if (value === null || value === undefined) {
    return <span>—</span>
  }

  if (STATE_KEYS.includes(name)) {
    const tag = getStateTag(value)

    if (tag) {
      return <Tag color={tag.color} style={TAG_STYLE}>{t(tag.label)}</Tag>
    }
  }

  if (isDateKey(name) && typeof value === 'string') {
    return <span>{formatDate(value) ?? value}</span>
  }

  if (typeof value === 'boolean') {
    return <span>{value ? '✓' : '✗'}</span>
  }

  if (typeof value === 'object') {
    return (
      <pre style={{ margin: 0, maxHeight: '220px', overflow: 'auto', fontSize: '11px' }}>
        {JSON.stringify(value, null, 2)}
      </pre>
    )
  }

  return <span>{String(value)}</span>
}

const AphmAnomalyDrawer = ({ anomaly = null, loading = false, onClose }) => {
  const t = useSelector(selectTranslate)

  const internship = anomaly?.internship ?? null
  const duplicateOf = anomaly?.duplicateOf ?? null
  const student = anomaly?.student ?? null
  const sector = anomaly?.sector ?? null
  const snapshot = anomaly?.snapshot ?? {}
  const snapshotKeys = Object.keys(snapshot).filter(key => !isRedundantKey(key, snapshot))

  return (
    <Drawer
      title={anomaly ? t(getTypeLabel(anomaly.type)) : ''}
      width={STANDARD_DRAWER_WIDTH}
      visible={anomaly !== null}
      onClose={onClose}
      zIndex={5000}
      destroyOnClose
    >
      {loading
        ? <div className='flex-row justify-content-center pd-16'><Spin /></div>
        : anomaly && (
          <div className='flex-col'>
            <div className='flex-row mb-8'>
              <Tag color={getSeverityTag(anomaly.severity).color} style={TAG_STYLE}>
                {t(getSeverityTag(anomaly.severity).label)}
              </Tag>
              <Tag color={getStatusTag(anomaly.status).color} style={TAG_STYLE}>
                {t(getStatusTag(anomaly.status).label)}
              </Tag>
            </div>

            <Descriptions size='small' column={1} bordered>
              <Descriptions.Item label={t('aphm_anomaly.aphm_id')}>{anomaly.aphmId ?? '—'}</Descriptions.Item>
              <Descriptions.Item label={t('aphm_anomaly.job_id')}>{anomaly.jobId ?? '—'}</Descriptions.Item>
              <Descriptions.Item label={t('aphm_anomaly.occurrences')}>{anomaly.occurrences}</Descriptions.Item>
              <Descriptions.Item label={t('aphm_anomaly.last_seen_at')}>{formatStampDateTime(anomaly.lastSeenAt) ?? '—'}</Descriptions.Item>
              <Descriptions.Item label={t('data_table.column_title.creation_date')}>{formatStampDate(anomaly.createdAt) ?? '—'}</Descriptions.Item>
            </Descriptions>

            {(internship || student || sector) && (
              <>
                <Divider orientation='left'>{t('aphm_anomaly.internship_section')}</Divider>
                <Descriptions size='small' column={1} bordered>
                  {student && (
                    <Descriptions.Item label={t('common.student')}>
                      {[student.firstname, student.lastname].filter(Boolean).join(' ') || student.id}
                    </Descriptions.Item>
                  )}
                  {sector && <Descriptions.Item label={t('common.sector')}>{sector.name}</Descriptions.Item>}
                  {internship && (
                    <Descriptions.Item label={t('aphm_anomaly.internship_id')}>{internship.id}</Descriptions.Item>
                  )}
                  {internship && (
                    <Descriptions.Item label={t('common.period')}>
                      {[formatDate(internship.startDate), formatDate(internship.endDate)].filter(Boolean).join(' → ') || '—'}
                    </Descriptions.Item>
                  )}
                  {duplicateOf && (
                    <Descriptions.Item label={t('aphm_anomaly.collides_with')}>{duplicateOf.id}</Descriptions.Item>
                  )}
                </Descriptions>
              </>
            )}

            {snapshotKeys.length > 0 && (
              <>
                <Divider orientation='left'>{t('aphm_anomaly.snapshot_section')}</Divider>
                <Descriptions size='small' column={1} bordered>
                  {snapshotKeys.map(key => (
                    <Descriptions.Item key={key} label={t('aphm_anomaly.snapshot.' + key)}>
                      <SnapshotValue name={key} value={snapshot[key]} />
                    </Descriptions.Item>
                  ))}
                </Descriptions>
              </>
            )}

            {anomaly.handledComment && (
              <>
                <Divider orientation='left'>{t('aphm_anomaly.decision_section')}</Divider>
                <Descriptions size='small' column={1} bordered>
                  <Descriptions.Item label={t('common.comment')}>{anomaly.handledComment}</Descriptions.Item>
                  <Descriptions.Item label={t('aphm_anomaly.handled_at')}>{formatStampDateTime(anomaly.handledAt) ?? '—'}</Descriptions.Item>
                  {anomaly.handledBy && (
                    <Descriptions.Item label={t('aphm_anomaly.handled_by')}>
                      {[anomaly.handledBy.firstname, anomaly.handledBy.lastname].filter(Boolean).join(' ')}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </>
            )}
          </div>
        )}
    </Drawer>
  )
}

export default AphmAnomalyDrawer
